import React from 'react';
import styled from 'styled-components/macro';
import { Text } from 'grommet';
import { colorValue, defaultTransition } from '../../../../lib/theme';
import { useSidebarContext } from '../Sidebar/SidebarContext';
import { useRouteMatch } from 'react-router-dom';

import { base } from '@sdxcorp/sdx-dds-gui-lib';

const {
  atoms: {
    Link: { BaseLink, linkBaseStyle },
  },
} = base;

interface Props {
  testId: string;
  label: string;
  to?: string;
  match: string;
  matchExact?: boolean;
}

interface InternalProps {
  depth?: number;
  icon?: JSX.Element;
  indicator?: JSX.Element;
  onClick?: React.MouseEventHandler;
  isLink?: boolean;
}

export const StyledIndicator = styled.div<{ matches: boolean }>`
  transition: ${defaultTransition('background-color')};
  width: 4px;
  background-color: ${props => (props.matches ? props.theme.global.colors['six-red'] : props.theme.global.colors.transparent)};
`;

const StyledText = styled(Text)<{ matches: boolean; depth: number }>`
  color: ${props => (props.matches ? props.theme.global.colors.white : props.theme.global.colors['grey-50'])};
  font-weight: ${props => (props.depth === 0 || props.matches ? 700 : 400)};
  font-size: 15px;
  text-transform: ${props => (props.depth === 0 ? 'uppercase' : 'normal')};
  margin-left: 8px;
`;

interface StyledSidebarLinkProps {
  testId: string;
  matches: boolean;
}

export const StyledSidebarLink = styled(({ matches, testId, ...rest }) => {
  return <BaseLink {...rest} />;
}).attrs(({ testId }: StyledSidebarLinkProps) => ({
  underline: false,
  'data-testid': `button.${testId}`.toLocaleLowerCase(),
}))<StyledSidebarLinkProps>`
  ${linkBaseStyle}
  padding: 16px 24px 16px 20px;
  width: calc(100% - 44px);

  &:hover {
    background-image: none;
    background-color: ${({ matches, theme }) => (!matches ? colorValue(theme, c => c['grey-85']) : 'colorValue')};

    ${StyledText} {
      color: ${({ matches, theme }) => (!matches ? colorValue(theme, c => c.white) : 'colorValue')};
    }
    ${StyledIndicator} {
      background-color: ${({ matches, theme }) => (!matches ? colorValue(theme, c => c['grey-85']) : 'colorValue')};
    }

    svg path,
    svg polygon {
      fill: ${props => colorValue(props.theme, c => c.white)};
    }
  }

  svg path,
  svg polygon {
    fill: ${props => (props.matches ? props.theme.global.colors.white : props.theme.global.colors['grey-50'])};
  }

  &,
  & * {
    transition: ${defaultTransition('all')};
  }
  &:active  {
    background-image: none;
  }

  display: flex;
  flex-direction: row;
  text-decoration: none;
  justify-content: space-between;
  align-items: center;
  overflow: 'visible';
  &:focus {
    background-image: none;
    background-color: transparent;
  }
`;

const StyledPlaceholderIcon = styled.svg`
  width: 24px;
  height: 24px;
`;

const StyledSidebarLinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  text-decoration: none;
  align-items: center;
  height: 24px;
  position: relative;
  width: 100%;
`;

export const InternalItem: React.FC<Props & InternalProps> = ({
  depth = 1,
  label,
  icon,
  indicator,
  to,
  match,
  matchExact = false,
  onClick,
  isLink = true,
  testId,
}) => {
  const matchResult = useRouteMatch(match);
  const matches = !!matchResult && (matchResult.isExact || matchExact === false);
  const { sidebarExpanded, onLinkClick } = useSidebarContext();
  const handleClick: React.MouseEventHandler = e => {
    if (onClick) {
      onClick(e);
    }
    if (isLink) {
      onLinkClick();
    }
  };
  return (
    <StyledSidebarLink testId={testId} matches={matches} to={to || match} onClick={handleClick}>
      <StyledSidebarLinkContainer>
        {icon || <StyledPlaceholderIcon />}
        {sidebarExpanded && (
          <StyledText depth={depth} matches={matches}>
            {label}
          </StyledText>
        )}
      </StyledSidebarLinkContainer>
      {sidebarExpanded && indicator}
    </StyledSidebarLink>
  );
};

const SidebarLinkItem: React.FC<Props> = props => <InternalItem {...props} />;

export default SidebarLinkItem;
